import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useTasks } from '../stores/tasks.js';
import { useProjects } from '../stores/projects.js';
import { TaskList } from '../components/TaskList.js';
import { ActionButtons } from '../components/ActionButtons.js';
import { useUi } from '../stores/ui.js';
import { Button } from '../ui/Button.js';
import { teinte } from '../lib/teinte.js';

export function Project(): React.ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  const { tasks, loading, load } = useTasks();
  const project = useProjects((s) => (projectId ? s.find(projectId) : undefined));
  const openProjectForm = useUi((s) => s.openProjectForm);

  useEffect(() => {
    if (projectId) load('project', { projectId });
  }, [projectId, load]);

  const sorted = useMemo(() => {
    return [...tasks].sort((a, b) => {
      if (a.dueDate === null && b.dueDate === null) return 0;
      if (a.dueDate === null) return 1;
      if (b.dueDate === null) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    });
  }, [tasks]);

  return (
    <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4 md:py-6 space-y-4">
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-2xl flex items-center gap-2">
          {project && <span className="point point-grand" style={teinte(project.color)} />}
          {project?.name ?? 'Project'}
          {/* Le crayon a suivi les projets hors de la sidebar (ADR 0007) : renommer un
              projet se fait depuis le projet lui-même, là où on est déjà. */}
          {project && (
            <Button
              variant="quiet"
              size="icon-small"
              onClick={() => openProjectForm(project.projectId)}
              aria-label={`Edit ${project.name}`}
            >
              <Pencil size={16} aria-hidden />
            </Button>
          )}
        </h1>
        <ActionButtons defaultProjectId={projectId ?? null} />
      </div>

      <TaskList
        tasks={sorted}
        loading={loading}
        hideProject
        showSubtitle
        emptyMessage="No tasks in this project."
      />
    </div>
  );
}
